import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Alert,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { logger } from '../../../utils/logger';
import { API_BASE_URL } from '../../../config/api';

const DashboardSettings: React.FC = () => {
  const [hideSidebarNotifications, setHideSidebarNotifications] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Fetch user preferences from API
    const fetchUserPreferences = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          logger.debug('No token found, setting loading to false');
          setLoading(false);
          return;
        }

        logger.debug('Fetching user preferences from API...');
        const response = await axios.get(`${API_BASE_URL}/users/preferences`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        logger.debug('=== PREFERENCES FETCH RESPONSE ===');
        logger.debug('Full response:', JSON.stringify(response.data, null, 2));
        logger.debug('Response success:', response.data?.success);
        logger.debug('Response preferences:', response.data?.preferences);

        if (response.data?.success) {
          const preferences = response.data.preferences || {};
          logger.debug('Preferences object:', preferences);
          const hideNotificationsValue = preferences.hideSidebarNotifications;
          logger.debug('hideSidebarNotifications value:', hideNotificationsValue);
          logger.debug('Type of value:', typeof hideNotificationsValue);
          logger.debug('Is undefined?', hideNotificationsValue === undefined);
          logger.debug('Is null?', hideNotificationsValue === null);
          
          // Handle various data types: true, 1, "1", "true", "True"
          // Also handle false, 0, "0", "false", "False", null, undefined
          let hideNotifications = false;
          if (hideNotificationsValue !== undefined && hideNotificationsValue !== null) {
            hideNotifications = hideNotificationsValue === true || 
                               hideNotificationsValue === 1 || 
                               hideNotificationsValue === '1' || 
                               hideNotificationsValue === 'true' ||
                               hideNotificationsValue === 'True';
          }
          
          logger.debug('=== FINAL STATE ===');
          logger.debug('Raw preference value:', hideNotificationsValue);
          logger.debug('Evaluated hideNotifications:', hideNotifications);
          logger.debug('Setting state to:', hideNotifications);
          
          setHideSidebarNotifications(hideNotifications);
        } else {
          logger.debug('Response was not successful, defaulting to false');
          logger.debug('Response data:', response.data);
          // If no preferences exist yet, default to false
          setHideSidebarNotifications(false);
        }
      } catch (err: any) {
        console.error('=== ERROR FETCHING PREFERENCES ===');
        console.error('Error:', err);
        console.error('Error response:', err.response?.data);
        // On error, default to false (show notifications)
        setHideSidebarNotifications(false);
      } finally {
        logger.debug('Setting loading to false');
        setLoading(false);
      }
    };

    fetchUserPreferences();

    // Listen for preference changes from other components
    const handlePreferenceChange = (event: CustomEvent) => {
      setHideSidebarNotifications(event.detail.hide);
    };

    window.addEventListener('sidebarNotificationsPreferenceChanged', handlePreferenceChange as EventListener);

    return () => {
      window.removeEventListener('sidebarNotificationsPreferenceChanged', handlePreferenceChange as EventListener);
    };
  }, []);

  const handleToggle = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.checked;
    const previousValue = hideSidebarNotifications;
    
    // Optimistically update UI
    setHideSidebarNotifications(newValue);
    setSaving(true);
    setError(null);
    setSuccess(false);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found');
      }

      logger.debug('Saving preference:', { hideSidebarNotifications: newValue });

      // Save to database via API
      const response = await axios.put(
        `${API_BASE_URL}/users/preferences`,
        {
          preferences: {
            hideSidebarNotifications: newValue
          }
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      logger.debug('Save preference response:', response.data);

      // Verify the saved value matches what we sent
      if (response.data?.success) {
        const savedPreferences = response.data.preferences || {};
        const savedValueRaw = savedPreferences.hideSidebarNotifications;
        // Handle various data types: true, 1, "1", "true", "True"
        const savedValue = savedValueRaw === true || 
                          savedValueRaw === 1 || 
                          savedValueRaw === '1' || 
                          savedValueRaw === 'true' ||
                          savedValueRaw === 'True';
        
        logger.debug('Saved preference value:', savedValueRaw, 'Evaluated to:', savedValue);
        
        // Ensure UI matches saved value
        setHideSidebarNotifications(savedValue);
        
        // Trigger a custom event so AdminLayout can update immediately
        window.dispatchEvent(new CustomEvent('sidebarNotificationsPreferenceChanged', {
          detail: { hide: savedValue }
        }));

        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
      } else {
        throw new Error('Save was not successful');
      }
    } catch (err: any) {
      console.error('Error saving preference:', err);
      const errorMessage = err.response?.data?.message || err.response?.data?.error || err.message || 'Failed to save preference. Please try again.';
      setError(errorMessage);
      
      // Revert to previous value on error
      setHideSidebarNotifications(previousValue);
      
      // Also refresh from server to get actual state
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const refreshResponse = await axios.get(`${API_BASE_URL}/users/preferences`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (refreshResponse.data?.success) {
            const preferences = refreshResponse.data.preferences || {};
            const hideNotificationsValue = preferences.hideSidebarNotifications;
            // Handle various data types: true, 1, "1", "true", "True"
            const hideNotifications = hideNotificationsValue === true || 
                                       hideNotificationsValue === 1 || 
                                       hideNotificationsValue === '1' || 
                                       hideNotificationsValue === 'true' ||
                                       hideNotificationsValue === 'True';
            setHideSidebarNotifications(hideNotifications);
          }
        }
      } catch (refreshErr) {
        console.error('Error refreshing preferences after save error:', refreshErr);
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Dashboard Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Customize your dashboard experience and notification preferences.
      </Typography>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(false)}>
          Settings saved successfully!
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <FormControlLabel
          control={
            <Switch
              checked={hideSidebarNotifications}
              onChange={handleToggle}
              disabled={saving || loading}
              color="primary"
            />
          }
          label={
            <Box>
              <Typography variant="body1" fontWeight={500}>
                Hide Sidebar Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                When enabled, notification badges will be hidden from the sidebar menu items.
                You can still access all features, but the notification counts won't be displayed.
              </Typography>
            </Box>
          }
          sx={{ alignItems: 'flex-start' }}
        />
      </Paper>
    </Box>
  );
};

export default DashboardSettings;

